import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { resolveArrivalTime } from '@/lib/schedule';
import type { Booking, Seat, Schedule, Route, Bus } from '@/types/database';
import type { PublicBus } from '@/services/routeService';

// Loosely-typed handle to the same client, for tables/RPCs that aren't in the
// generated Database types. Identical runtime object — relaxes only the
// compile-time typing so call sites don't need `as any`. (See adminService.ts.)
const db = supabase as unknown as SupabaseClient;

// Call an RPC returning a known shape without `any`. `db.rpc` is accessed and
// invoked in one expression, so `this` stays bound to the client.
function callRpc<T>(fn: string, args?: Record<string, unknown>) {
  return (db.rpc as unknown as (
    f: string, a?: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { code?: string; message?: string } | null }>)(fn, args);
}

// ============================================================
// Types
// ============================================================

export interface SeatInfo {
  id: string;
  seatNumber: string;
  rowLabel: string;
  seatType: 'standard' | 'premium' | 'ladies';
  isBooked: boolean;
  status: 'available' | 'holding' | 'sold';
}

export interface BookingDetails {
  id: string;
  status: string;
  travelDate: string;
  totalFare: number;
  passengerName: string;
  passengerPhone: string;
  passengerEmail: string | null;
  boardingPoint: string;
  droppingPoint: string;
  createdAt: string;
  route: { origin: string; destination: string; distanceKm: number; durationMinutes: number };
  schedule: { departureTime: string; arrivalTime: string };
  bus: { name: string; type: string; amenities: string[] };
  seats: { seatNumber: string; fare: number }[];
  payment: { method: string; status: string; amount: number; paidAt: string | null; transactionId: string | null } | null;
  ticketToken: string | null;
}

export interface UserBooking {
  id: string;
  bookingId: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  from: string;
  to: string;
  date: string;
  departureTime: string;
  arrivalTime: string;
  coachName: string;
  coachType: string;
  seats: string[];
  totalFare: number;
  boardingPoint: string;
  droppingPoint: string;
  hasRated?: boolean;
}

/** One crew member on a trip, as `rpc_my_booking_crew` returns them. */
export interface BookingCrewMember {
  id: string;
  name: string;
  role: 'driver' | 'staff' | 'supervisor';
}

export interface CreateBookingParams {
  scheduleId: string;
  travelDate: string;
  seatIds: string[];
  boarding: string;
  dropping: string;
  passengerName: string;
  passengerPhone: string;
  passengerEmail?: string;
}

// ============================================================
// Helper — map bus name to coach type label
// ============================================================
function mapBusType(busName: string): string {
  if (busName.toLowerCase().includes('non-ac')) return 'Non-AC';
  return 'AC';
}

/**
 * Maximum tickets one person may hold on a single departure.
 *
 * The server is the authority — trg_enforce_passenger_seat_cap counts seats by
 * passenger phone across every booking on the same schedule and date, so the
 * cap survives a split purchase. This constant keeps the funnel in step so a
 * passenger is stopped with a readable message rather than a trigger error.
 */
export const MAX_TICKETS_PER_PERSON = 3;

// Reservation timeout in minutes — pending bookings older than this are auto-cancelled
const RESERVATION_TIMEOUT_MINUTES = 15;

// ============================================================
// 0. cleanupStaleBookings — cancel pending bookings older than timeout
// ============================================================
export async function cleanupStaleBookings(scheduleId: string, date: string): Promise<void> {
  try {
    // Use the safe SQL function that excludes bookings with successful payments
    // (prevents race condition where cleanup cancels a booking mid-payment-webhook)
    await callRpc('cleanup_stale_pending_bookings', {
      p_timeout_minutes: RESERVATION_TIMEOUT_MINUTES,
    });
  } catch (err) {
    // Non-critical — log and continue
  }
}

// ============================================================
// 1. getSeatAvailability — real-time seat status for a schedule + date
// ============================================================
export async function getSeatAvailability(
  scheduleId: string,
  date: string
): Promise<{ seats: SeatInfo[]; bookedSeatIds: Set<string>; occupancyKnown: boolean }> {
  try {
    // NOTE: Stale booking cleanup removed from client-side — SQL cron runs every 2 min.
    // Having it here allowed unauthenticated users to trigger DB writes (BUG-07).

    // Get bus_id from the schedule
    const { data: schedule, error: schedErr } = await supabase
      .from('schedules')
      .select('bus_id')
      .eq('id', scheduleId)
      .single();

    if (schedErr || !schedule) {
      return { seats: [], bookedSeatIds: new Set(), occupancyKnown: false };
    }

    // Get all active seats for this bus (only columns used by SeatInfo)
    const { data: seats, error: seatsErr } = await supabase
      .from('seats')
      .select('id, seat_number, row_label, seat_type')
      .eq('bus_id', schedule.bus_id)
      .eq('is_active', true)
      .order('row_label')
      .order('seat_number');

    if (seatsErr || !seats) {
      return { seats: [], bookedSeatIds: new Set(), occupancyKnown: false };
    }

    const soldSeatIds = new Set<string>();
    const holdingSeatIds = new Set<string>();
    const bookedSeatIds = new Set<string>();

    // The old implementation selected every authenticated user's booking row
    // to derive seat state, which exposed passenger PII through RLS. The RPC
    // returns only seat ids and status, with the join performed server-side.
    const { data: occupiedSeats, error: occupancyErr } = await callRpc<Array<{ seat_id: string; status: string }>>(
      'rpc_seat_occupancy',
      { p_schedule_id: scheduleId, p_travel_date: date },
    );

    // `rpc_seat_occupancy` requires a signed-in caller, so a logged-out visitor
    // gets 401 here. Silently treating that as "nothing is booked" painted
    // every sold seat as free and only failed at checkout, where the server
    // rejects the seat. Report that occupancy is unknown and let the caller say
    // so, rather than showing a confidently wrong seat map.
    const occupancyKnown = !occupancyErr;

    (occupiedSeats || []).forEach((bs) => {
      if (bs.status === 'confirmed') {
        soldSeatIds.add(bs.seat_id);
        bookedSeatIds.add(bs.seat_id);
      } else if (bs.status === 'pending') {
        holdingSeatIds.add(bs.seat_id);
        bookedSeatIds.add(bs.seat_id);
      }
    });

    const seatInfos: SeatInfo[] = (seats as Seat[]).map(s => {
      const isSold = soldSeatIds.has(s.id);
      const isHolding = holdingSeatIds.has(s.id);
      const status = isSold ? 'sold' : isHolding ? 'holding' : 'available';
      return {
        id: s.id,
        seatNumber: s.seat_number,
        rowLabel: s.row_label,
        seatType: s.seat_type as SeatInfo['seatType'],
        isBooked: status !== 'available',
        status,
      };
    });

    return { seats: seatInfos, bookedSeatIds, occupancyKnown };
  } catch (err) {
    return { seats: [], bookedSeatIds: new Set(), occupancyKnown: false };
  }
}

// ============================================================
// 2. getScheduleDetails — schedule with route + bus info
// ============================================================
export async function getScheduleDetails(scheduleId: string) {
  try {
    const { data, error } = await supabase
      .from('schedules')
      // `buses_public`: seat selection needs the class to price the fare, not
      // the coach identity. See the buses_public view for why.
      .select(`
        *,
        routes (*),
        buses_public (*)
      `)
      .eq('id', scheduleId)
      .single();

    if (error || !data) {
      return null;
    }

    return data as unknown as Schedule & { routes: Route; buses_public: PublicBus | null };
  } catch (err) {
    return null;
  }
}

// ============================================================
// 2b. getRouteBoardingDropping — fetch boarding/dropping points from route_counters
// ============================================================
export async function getRouteBoardingDropping(scheduleId: string): Promise<{ boarding: string[]; dropping: string[] }> {
  try {
    // Get route_id from schedule
    const { data: schedule, error: schedErr } = await supabase
      .from('schedules')
      .select('route_id')
      .eq('id', scheduleId)
      .single();

    if (schedErr || !schedule?.route_id) {
      return { boarding: [], dropping: [] };
    }

    // Fetch visible counters for this route (respect is_visible_to_customer)
    const { data: counters, error: counterErr } = await supabase
      .from('route_counters')
      .select('name, custom_point_name, is_boarding_allowed, is_dropping_allowed, is_visible_to_customer, counter_type, sort_order')
      .eq('route_id', schedule.route_id)
      .eq('status', 'Active')
      .order('sort_order', { ascending: true });

    if (counterErr) {
      return { boarding: [], dropping: [] };
    }

    if (!counters || counters.length === 0) return { boarding: [], dropping: [] };

    // Only show customer-visible stops; use the DB boolean flags directly
    type CounterRow = {
      name: string; custom_point_name?: string | null;
      is_visible_to_customer?: boolean | null;
      is_boarding_allowed?: boolean | null;
      is_dropping_allowed?: boolean | null;
    };
    const rows = counters as CounterRow[];
    const pointName = (c: CounterRow) => (c.custom_point_name && c.custom_point_name.trim()) || c.name;
    const visible = rows.filter((c) => c.is_visible_to_customer !== false);

    const boarding = visible
      .filter((c) => c.is_boarding_allowed === true)
      .map(pointName);

    const dropping = visible
      .filter((c) => c.is_dropping_allowed === true)
      .map(pointName);

    return { boarding, dropping };
  } catch (err) {
    return { boarding: [], dropping: [] };
  }
}

// ============================================================
// 3. createBooking — create a pending booking with seat assignments
// ============================================================
export async function createBooking(params: CreateBookingParams): Promise<{ id: string | null; error?: string }> {
  try {
    const {
      scheduleId, travelDate, seatIds,
      boarding, dropping,
      passengerName, passengerPhone, passengerEmail,
    } = params;

    // Enforce the per-person ticket cap
    if (seatIds.length > MAX_TICKETS_PER_PERSON) {
      return { id: null, error: `Maximum ${MAX_TICKETS_PER_PERSON} tickets per person.` };
    }

    if (seatIds.length === 0) {
      return { id: null, error: 'Please select at least one seat.' };
    }

    // Atomic booking via PostgreSQL RPC — uses FOR UPDATE row-level locking
    // to prevent double-bookings. Replaces the old multi-step JS approach
    // which had a TOCTOU race window between insert and conflict check.
    const { data, error } = await callRpc<string>('create_booking_atomic', {
      p_schedule_id: scheduleId,
      p_travel_date: travelDate,
      p_seat_ids: seatIds,
      p_boarding_point: boarding,
      p_dropping_point: dropping,
      p_passenger_name: passengerName,
      p_passenger_phone: passengerPhone,
      p_passenger_email: passengerEmail || null,
    });

    if (error) {
      // The RPC raises unique_violation if seats are already booked
      if (error.code === '23505' || error.message?.includes('already booked')) {
        return { id: null, error: 'One or more seats were just taken by another passenger. Please choose different seats.' };
      }

      // Surface the guards the RPC raises on purpose. Collapsing these into
      // "Please try again" told a staff account to retry something that can
      // never succeed, and hid "this departure does not run on that date"
      // behind the same dead end. 42501 is the authorisation guards
      // (staff-barred-from-self-serve), P0002 the validation ones (unknown
      // schedule or route, counter-only departure, wrong day of week).
      // Both carry messages written for a passenger to read.
      if ((error.code === '42501' || error.code === 'P0002') && error.message) {
        return { id: null, error: error.message };
      }

      return { id: null, error: 'Failed to create booking. Please try again.' };
    }

    return { id: data as string }; // Returns the new booking UUID
  } catch (err) {
    return { id: null, error: 'Failed to create booking. Please try again.' };
  }
}

// ============================================================
// NOTE: confirmBooking() was intentionally removed (Bug #02).
// It directly set booking status to 'confirmed' and inserted a payment
// record client-side — a payment bypass. Counter payments now use the
// confirm_counter_booking RPC, and online payments use the
// payment-callback edge function. Do not re-add.
// ============================================================
// 5. getBookingDetails — full booking with all joined data
// ============================================================
export async function getBookingDetails(bookingId: string): Promise<BookingDetails | null> {
  try {
    // Fetch booking with schedule+route+bus
    const { data: booking, error: bookingErr } = await supabase
      .from('bookings')
      .select(`
        id, status, travel_date, total_fare, passenger_name,
        passenger_phone, passenger_email, boarding_point,
        dropping_point, created_at,
        schedules (
          departure_time, arrival_time,
          routes (origin, destination, distance_km, duration_minutes),
          buses_public (service_class, type, amenities)
        )
      `)
      .eq('id', bookingId)
      .single();

    if (bookingErr || !booking) {
      return null;
    }

    // Fetch booking seats with seat info — isolated try-catch so partial failure
    // doesn't lose the entire booking (RLS on booking_seats may fail for edge cases)
    type SeatRow = { fare: number | null; seats?: { seat_number?: string } | null };
    let bookingSeats: SeatRow[] = [];
    try {
      const { data } = await supabase
        .from('booking_seats')
        .select(`
          fare,
          seats (seat_number)
        `)
        .eq('booking_id', bookingId);
      bookingSeats = (data as SeatRow[] | null) || [];
    } catch { /* booking_seats fetch failed — continue with empty */ }

    // Fetch payment — isolated try-catch
    type PaymentRow = { method?: string; status?: string; amount?: number; paid_at?: string | null; transaction_id?: string | null };
    let payment: PaymentRow | null = null;
    try {
      const { data: payments } = await supabase
        .from('payments')
        .select('method, status, amount, paid_at, transaction_id')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })
        .limit(1);
      payment = payments && payments.length > 0 ? payments[0] : null;
    } catch { /* payment fetch failed — continue without */ }

    type ScheduleJoin = {
      departure_time?: string; arrival_time?: string;
      routes?: { origin?: string; destination?: string; distance_km?: number; duration_minutes?: number } | null;
      buses_public?: { service_class?: string; type?: string; amenities?: string[] } | null;
    };
    const schedule = (booking as { schedules?: ScheduleJoin }).schedules;
    const route = schedule?.routes;
    const bus = schedule?.buses_public;

    const details: BookingDetails = {
      id: booking.id,
      status: booking.status,
      travelDate: booking.travel_date,
      totalFare: booking.total_fare ?? 0,
      passengerName: booking.passenger_name || '',
      passengerPhone: booking.passenger_phone || '',
      passengerEmail: booking.passenger_email || null,
      boardingPoint: booking.boarding_point || '',
      droppingPoint: booking.dropping_point || '',
      createdAt: booking.created_at || '',
      route: {
        origin: route?.origin || '',
        destination: route?.destination || '',
        distanceKm: route?.distance_km ?? 0,
        durationMinutes: route?.duration_minutes ?? 0,
      },
      schedule: {
        departureTime: schedule?.departure_time?.substring(0, 5) || '',
        // Same guard search uses. Without it the e-ticket printed
        // "05:22 Feni -> 05:22 Maniknagar" on a five-hour trip.
        arrivalTime: resolveArrivalTime(
          schedule?.departure_time, schedule?.arrival_time, route?.duration_minutes,
        ),
      },
      bus: {
        // The tier, not the coach. `MyTicketDetails` replaces this with the
        // real coach once `rpc_my_departure_bus` says the reveal clock passed.
        name: bus?.service_class || '',
        type: bus?.type || '',
        amenities: Array.isArray(bus?.amenities) ? bus.amenities as string[] : [],
      },
      seats: bookingSeats.map((bs) => ({
        seatNumber: bs.seats?.seat_number || '',
        fare: bs.fare ?? 0,
      })),
      payment: payment ? {
        method: payment.method || '',
        status: payment.status || '',
        amount: payment.amount ?? booking.total_fare ?? 0,
        paidAt: payment.paid_at || null,
        transactionId: payment.transaction_id || null,
      } : null,
      ticketToken: (booking as { ticket_token?: string | null }).ticket_token || null,
    };

    // Cache for offline access
    cacheTicket(bookingId, details);
    return details;
  } catch (err) {
    return null;
  }
}


// ============================================================
// 6. getUserBookings — all bookings for a user (dashboard)
// ============================================================
export async function getUserBookings(userId: string): Promise<UserBooking[]> {
  try {
    const { data: bookings, error } = await db
      .from('bookings')
      .select(`
      *,
      schedules (
        departure_time,
        arrival_time,
        routes (origin, destination, duration_minutes),
        buses_public (service_class, type)
      ),
      booking_seats (
        seats (seat_number)
      ),
      trip_ratings (id)
    `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error || !bookings) {
      return [];
    }

    type BusJoin = { service_class?: string; type?: string };
    type BookingRow = {
      id: string; status: string; travel_date: string; total_fare: number;
      boarding_point: string; dropping_point: string;
      schedules?: {
        departure_time?: string; arrival_time?: string;
        routes?: { origin?: string; destination?: string; duration_minutes?: number } | null;
        buses_public?: BusJoin | null;
      } | null;
      booking_seats?: { seats?: { seat_number?: string } | null }[] | null;
      trip_ratings?: { id: string }[] | null;
    };
    const rows = (bookings || []) as unknown as BookingRow[];

    // Crew used to be resolved here, by embedding the bus's assigned_* columns
    // and reading the personnel tables. Both are staff-only now: the rating
    // modal loads crew on demand via `getMyBookingCrew`, which applies the same
    // reveal clock as the coach number.
    return rows.map((b) => {
      const schedule = b.schedules;
      const route = schedule?.routes;
      const bus = schedule?.buses_public;
      const seatNumbers = (b.booking_seats || []).map((sr) => sr.seats?.seat_number || '').filter(Boolean);
      const hasRated = Array.isArray(b.trip_ratings) && b.trip_ratings.length > 0;

      return {
        id: b.id,
        bookingId: `STR-${b.id.slice(0, 8).toUpperCase()}`,
        status: b.status as UserBooking['status'],
        from: route?.origin || '',
        to: route?.destination || '',
        date: b.travel_date,
        departureTime: schedule?.departure_time?.substring(0, 5) || '',
        arrivalTime: resolveArrivalTime(
          schedule?.departure_time, schedule?.arrival_time, route?.duration_minutes,
        ),
        coachName: bus?.service_class || 'Star Line',
        coachType: bus?.type || '',
        seats: seatNumbers,
        totalFare: b.total_fare,
        boardingPoint: b.boarding_point,
        droppingPoint: b.dropping_point,
        hasRated,
      };
    });
  } catch (err) {
    return [];
  }
}

// ============================================================
// 7. Cancellation & Refund
// ============================================================

/** Refund preview data returned by preview_cancellation_refund RPC */
export interface CancellationPreview {
  eligible: boolean;
  error?: string;
  refund_amount: number;
  refund_tier: 'full' | 'half' | 'quarter' | 'none';
  refund_percentage: number;
  hours_until_departure: number;
  processing_fee: number;
  service_fee: number;
  original_fare: number;
  payment_method: string;
  /** The tier's published name, e.g. "Quarter refund". Absent on legacy rows. */
  tier_label?: string;
  /** Which version of the ladder priced this quote. */
  policy_version?: number;
}

/** Result from cancel_booking_with_refund RPC */
export interface CancellationResult {
  success: boolean;
  error?: string;
  refund_id?: string;
  refund_amount?: number;
  refund_tier?: string;
  hours_until_departure?: number;
  processing_fee?: number;
  service_fee?: number;
  original_fare?: number;
  payment_method?: string;
  auto_refund?: boolean;
}

/**
 * Preview the refund amount and tier before confirming cancellation.
 * This is a read-only operation — no side effects.
 */
export async function previewCancellation(bookingId: string, userId: string): Promise<CancellationPreview | null> {
  try {
    const { data, error } = await callRpc<CancellationPreview>('preview_cancellation_refund', {
      p_booking_id: bookingId,
    });

    if (error || !data) {

      return null;
    }

    return data;
  } catch (err) {

    return null;
  }
}

/**
 * Cancel a booking with automatic refund computation.
 * Uses the atomic cancel_booking_with_refund PostgreSQL function which:
 * - Verifies ownership and eligibility
 * - Checks departure time hasn't passed
 * - Computes refund tier (100%/50%/25%/0%)
 * - Cancels booking + creates refund request in one transaction
 * - Prevents duplicate cancellations
 */
export async function cancelBooking(
  bookingId: string,
  userId: string,
  reason: string = 'User requested cancellation'
): Promise<CancellationResult> {
  try {
    const { data, error } = await callRpc<CancellationResult>('cancel_booking_with_refund', {
      p_booking_id: bookingId,
      p_reason: reason,
    });

    if (error) {

      return { success: false, error: error.message };
    }

    return data || { success: false, error: 'No response from server' };
  } catch (err) {

    return { success: false, error: 'An unexpected error occurred' };
  }
}

// ============================================================
// Offline Ticket Caching (localStorage)
// ============================================================
const TICKET_CACHE_PREFIX = 'sl_ticket_';
const TICKET_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

function cacheTicket(bookingId: string, data: BookingDetails): void {
  try {
    const entry = { data, cachedAt: Date.now() };
    localStorage.setItem(`${TICKET_CACHE_PREFIX}${bookingId}`, JSON.stringify(entry));
  } catch { /* quota exceeded — ignore */ }
}

export function getCachedTicket(bookingId: string): BookingDetails | null {
  try {
    const raw = localStorage.getItem(`${TICKET_CACHE_PREFIX}${bookingId}`);
    if (!raw) return null;
    const { data, cachedAt } = JSON.parse(raw);
    // Expired cache
    if (Date.now() - cachedAt > TICKET_CACHE_TTL) {
      localStorage.removeItem(`${TICKET_CACHE_PREFIX}${bookingId}`);
      return null;
    }
    return data as BookingDetails;
  } catch {
    return null;
  }
}

export function clearTicketCache(bookingId: string): void {
  localStorage.removeItem(`${TICKET_CACHE_PREFIX}${bookingId}`);
}

// ============================================================
// Signed QR Token — fetch HMAC ticket token from server
// ============================================================
export async function getTicketToken(bookingId: string): Promise<string | null> {
  try {
    const { data, error } = await callRpc<string>('generate_ticket_token', {
      p_booking_id: bookingId,
    });
    if (error || !data) return null;
    return data;
  } catch {
    return null;
  }
}

// ============================================================
// My Ticket Details — RLS-protected booking fetch for passengers
// Alias for getBookingDetails; RLS enforces user_id = auth.uid()
// ============================================================
export const getMyBookingDetails = getBookingDetails;

// ============================================================
// Guest Booking Lookup — calls SECURITY DEFINER RPC
// Bypasses RLS by verifying PNR + phone server-side.
// Returns only safe public booking fields.
// ============================================================
export async function guestLookupBooking(
  pnrFragment: string,
  phone: string
): Promise<BookingDetails | null> {
  try {
    type GuestBookingJson = {
      id?: string; status?: string; travelDate?: string; totalFare?: number;
      passengerName?: string; passengerPhone?: string;
      boardingPoint?: string; droppingPoint?: string; createdAt?: string;
      route?: { origin?: string; destination?: string; distanceKm?: number; durationMinutes?: number };
      schedule?: { departureTime?: string; arrivalTime?: string };
      bus?: { name?: string; type?: string; amenities?: string[] };
      seats?: { seatNumber?: string; fare?: number }[];
      payment?: { method?: string; status?: string; amount?: number; paidAt?: string | null } | null;
    };
    const { data, error } = await callRpc<GuestBookingJson>('guest_lookup_booking', {
      p_pnr_fragment: pnrFragment,
      p_phone: phone,
    });

    if (error || !data) {
      return null;
    }

    // Map the RPC JSON response to BookingDetails type
    const d = data;
    const details: BookingDetails = {
      id: d.id || '',
      status: d.status || '',
      travelDate: d.travelDate || '',
      totalFare: d.totalFare ?? 0,
      passengerName: d.passengerName || '',
      passengerPhone: d.passengerPhone || '',
      passengerEmail: null, // Not exposed to guests
      boardingPoint: d.boardingPoint || '',
      droppingPoint: d.droppingPoint || '',
      createdAt: d.createdAt || '',
      route: {
        origin: d.route?.origin || '',
        destination: d.route?.destination || '',
        distanceKm: d.route?.distanceKm ?? 0,
        durationMinutes: d.route?.durationMinutes ?? 0,
      },
      schedule: {
        departureTime: d.schedule?.departureTime || '',
        arrivalTime: d.schedule?.arrivalTime || '',
      },
      bus: {
        name: d.bus?.name || '',
        type: d.bus?.type || 'AC',
        amenities: Array.isArray(d.bus?.amenities) ? d.bus.amenities : [],
      },
      seats: Array.isArray(d.seats) ? d.seats.map((s) => ({
        seatNumber: s.seatNumber || '',
        fare: s.fare ?? 0,
      })) : [],
      payment: d.payment ? {
        method: d.payment.method || '',
        status: d.payment.status || '',
        amount: d.payment.amount ?? d.totalFare ?? 0,
        paidAt: d.payment.paidAt || null,
        transactionId: null, // Not exposed to guests
      } : null,
      ticketToken: null, // Not exposed to guests
    };

    return details;
  } catch (err) {
    return null;
  }
}

// ============================================================
// Public Ticket Share — no auth required
// Verifies HMAC token + returns public-safe booking fields
// Powers the /t/:bookingId?k=:token share page
// ============================================================
export interface PublicTicketInfo {
  valid: true;
  id: string;
  pnr: string;
  status: string;
  passengerName: string; // first name only
  travelDate: string;
  totalFare: number;
  boardingPoint: string;
  droppingPoint: string;
  origin: string;
  destination: string;
  departureTime: string;
  arrivalTime: string;
  /** Empty until the reveal clock passes — the server withholds it, not the UI. */
  busName: string;
  busType: string;
  busRevealed: boolean;
  /** ISO timestamp the number becomes visible. */
  busRevealAt?: string;
  seats: string[];
  amenities: string[];
}

export type PublicTicketResult =
  | PublicTicketInfo
  | { valid: false; error: 'not_found' | 'invalid_token' | 'unknown' };

export async function getPublicTicketByToken(
  bookingId: string,
  token: string
): Promise<PublicTicketResult> {
  try {
    const { data, error } = await callRpc<string | Record<string, unknown>>('get_public_ticket_info', {
      p_booking_id: bookingId,
      p_token: token,
    });

    if (error) {
      console.warn('[TicketShare] RPC error:', error.message, error.code);
      return { valid: false, error: 'unknown' };
    }
    if (!data) {
      console.warn('[TicketShare] RPC returned null data');
      return { valid: false, error: 'unknown' };
    }

    // The RPC returns JSON — Supabase auto-parses it
    type TicketJson = Partial<Omit<PublicTicketInfo, 'valid'>> & {
      valid?: boolean; error?: 'not_found' | 'invalid_token' | 'unknown';
    };
    const d = (typeof data === 'string' ? JSON.parse(data) : data) as TicketJson;

    if (!d.valid) {
      console.warn('[TicketShare] Token validation failed:', d.error);
      return { valid: false, error: d.error ?? 'unknown' };
    }

    return {
      valid: true,
      id: d.id ?? '',
      pnr: d.pnr ?? '',
      status: d.status ?? '',
      passengerName: d.passengerName ?? '',
      travelDate: d.travelDate ?? '',
      totalFare: d.totalFare ?? 0,
      boardingPoint: d.boardingPoint ?? '',
      droppingPoint: d.droppingPoint ?? '',
      origin: d.origin ?? '',
      destination: d.destination ?? '',
      departureTime: d.departureTime ?? '',
      arrivalTime: d.arrivalTime ?? '',
      busName: d.busName ?? '',
      busType: d.busType ?? '',
      // Default to hidden: an older server that omits the flag must not be
      // read as "revealed".
      busRevealed: d.busRevealed === true,
      busRevealAt: d.busRevealAt,
      seats: Array.isArray(d.seats) ? d.seats : [],
      amenities: Array.isArray(d.amenities) ? d.amenities : [],
    };
  } catch (err) {
    console.warn('[TicketShare] Unexpected error:', err);
    return { valid: false, error: 'unknown' };
  }
}

// ────────────────────────────────────────────────
// Public live trip tracking — same share token as the ticket above.
// Lets whoever holds the link (family, friends) watch the bus move
// without an account. Returns tracking:false when the trip has no
// tracking row yet, which is a valid state, not an error.
// ────────────────────────────────────────────────

export interface PublicTripTracking {
  valid: true;
  tracking: true;
  scheduleId: string;
  travelDate: string;
  status: string;
  currentStop: string | null;
  nextStop: string | null;
  stopsCompleted: number;
  totalStops: number;
  progressPercent: number;
  eta: string | null;
  lastUpdated: string;
  currentLat: number | null;
  currentLng: number | null;
  routeId: string | null;
  departureTime: string;
  arrivalTime: string;
  boardingPoint: string;
  droppingPoint: string;
}

export type PublicTripTrackingResult =
  | PublicTripTracking
  | { valid: true; tracking: false; boardingPoint: string; droppingPoint: string }
  | { valid: false; error: 'not_found' | 'invalid_token' | 'unknown' };

export async function getPublicTripTracking(
  bookingId: string,
  token: string
): Promise<PublicTripTrackingResult> {
  try {
    const { data, error } = await callRpc<string | Record<string, unknown>>('get_public_trip_tracking', {
      p_booking_id: bookingId,
      p_token: token,
    });

    if (error) {
      console.warn('[TripTracking] RPC error:', error.message, error.code);
      return { valid: false, error: 'unknown' };
    }
    if (!data) {
      console.warn('[TripTracking] RPC returned null data');
      return { valid: false, error: 'unknown' };
    }

    type TrackingJson = {
      valid?: boolean; tracking?: boolean;
      error?: 'not_found' | 'invalid_token' | 'unknown';
      scheduleId?: string; travelDate?: string; status?: string;
      currentStop?: string | null; nextStop?: string | null;
      stopsCompleted?: number; totalStops?: number; progressPercent?: number;
      eta?: string | null; lastUpdated?: string;
      currentLat?: number | null; currentLng?: number | null;
      routeId?: string | null; departureTime?: string; arrivalTime?: string;
      boardingPoint?: string; droppingPoint?: string;
    };
    const d = (typeof data === 'string' ? JSON.parse(data) : data) as TrackingJson;

    if (!d.valid) {
      return { valid: false, error: d.error ?? 'unknown' };
    }

    if (!d.tracking) {
      return {
        valid: true,
        tracking: false,
        boardingPoint: d.boardingPoint ?? '',
        droppingPoint: d.droppingPoint ?? '',
      };
    }

    return {
      valid: true,
      tracking: true,
      scheduleId: d.scheduleId ?? '',
      travelDate: d.travelDate ?? '',
      status: d.status ?? '',
      currentStop: d.currentStop ?? null,
      nextStop: d.nextStop ?? null,
      stopsCompleted: Number(d.stopsCompleted ?? 0),
      totalStops: Number(d.totalStops ?? 0),
      progressPercent: Number(d.progressPercent ?? 0),
      eta: d.eta ?? null,
      lastUpdated: d.lastUpdated ?? '',
      currentLat: d.currentLat === null || d.currentLat === undefined ? null : Number(d.currentLat),
      currentLng: d.currentLng === null || d.currentLng === undefined ? null : Number(d.currentLng),
      routeId: d.routeId ?? null,
      departureTime: d.departureTime ?? '',
      arrivalTime: d.arrivalTime ?? '',
      boardingPoint: d.boardingPoint ?? '',
      droppingPoint: d.droppingPoint ?? '',
    };
  } catch (err) {
    console.warn('[TripTracking] Unexpected error:', err);
    return { valid: false, error: 'unknown' };
  }
}




// ── Bus reveal and passenger notifications ──────────────────
//
// StarLine shows a passenger which bus they are on only shortly before it
// leaves. Note that `buses.name` ("Starline Gold-02") maps one-to-one to the
// registration ("DHAKA-METRO-G02"), so the gate covers the name too — showing
// one while hiding the other would reveal nothing less.

export interface DepartureBusReveal {
  revealed: boolean;
  reason?: 'too_early' | 'no_departure_assigned';
  /** When the number becomes visible. */
  reveal_at?: string;
  departure_at?: string;
  message?: string;
  /** Always available — the class is what the passenger booked, not a secret. */
  bus_type?: string | null;
  total_seats?: number | null;
  amenities?: string[] | null;
  /** Present only once `revealed` is true. */
  bus_name?: string | null;
  registration_number?: string | null;
  error?: string;
}

/** The bus for one of my bookings, if the clock says I may know yet. */
export async function getMyDepartureBus(bookingId: string): Promise<DepartureBusReveal | null> {
  const { data, error } = await (supabase.rpc as unknown as (
    f: string, a: Record<string, unknown>,
  ) => Promise<{ data: DepartureBusReveal | null; error: { message?: string } | null }>)(
    'rpc_my_departure_bus', { p_booking_id: bookingId },
  );
  if (error) throw new Error(error.message || 'Could not load bus details');
  return data;
}

/**
 * Who crewed my trip. Gated on the same clock as the coach number — knowing
 * tonight's driver is knowing tonight's coach — so this returns an empty crew
 * before the reveal. Used by the post-trip rating modal, where the crew is
 * always long since revealed.
 */
export async function getMyBookingCrew(bookingId: string): Promise<BookingCrewMember[]> {
  const { data, error } = await (supabase.rpc as unknown as (
    f: string, a: Record<string, unknown>,
  ) => Promise<{
    data: { revealed?: boolean; crew?: BookingCrewMember[]; error?: string } | null;
    error: { message?: string } | null;
  }>)('rpc_my_booking_crew', { p_booking_id: bookingId });
  if (error) throw new Error(error.message || 'Could not load crew');
  return data?.crew ?? [];
}

export interface PassengerNotification {
  id: string;
  kind: 'bus_assigned' | 'bus_changed' | 'departure_delayed' | 'departure_cancelled' | 'general';
  title: string;
  body: string;
  previous_value: string | null;
  new_value: string | null;
  booking_id: string | null;
  departure_id: string | null;
  read_at: string | null;
  created_at: string;
}

export async function getMyNotifications(opts: { limit?: number; unreadOnly?: boolean } = {}) {
  const { data, error } = await (supabase.rpc as unknown as (
    f: string, a: Record<string, unknown>,
  ) => Promise<{
    data: { unread: number; rows: PassengerNotification[] } | null;
    error: { message?: string } | null;
  }>)('rpc_my_notifications', {
    p_limit: opts.limit ?? 30,
    p_unread_only: opts.unreadOnly ?? false,
  });
  if (error) throw new Error(error.message || 'Could not load notifications');
  return data ?? { unread: 0, rows: [] };
}

export async function markNotificationsRead(ids?: string[]) {
  const { error } = await (supabase.rpc as unknown as (
    f: string, a: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message?: string } | null }>)(
    'rpc_mark_notifications_read', { p_ids: ids ?? null },
  );
  if (error) throw new Error(error.message || 'Could not update notifications');
}

// ────────────────────────────────────────────────
// Reschedule — move a ticket to another departure, free, once.
//
// Every rule lives in the RPCs (`reschedule_eligibility` is shared by all
// three), so nothing here decides eligibility. The client asks and renders.
// ────────────────────────────────────────────────

export interface RescheduleQuote {
  eligible: boolean;
  /** Why not, when `eligible` is false. Written for a passenger to read. */
  reason?: string | null;
  fee: number;
  /** Applications close this far ahead of the current departure. */
  apply_deadline?: string;
  window_from?: string;
  window_to?: string;
  departure_at?: string;
  seat_count?: number;
  times_moved?: number;
  error?: string;
}

export interface RescheduleOption {
  departure_id: string;
  schedule_id: string;
  service_date: string;
  departure_at: string;
  arrival_at: string;
  bus_type: string;
  total_seats: number;
  fare_per_seat: number;
  seats_available: number;
}

async function rescheduleRpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await (supabase.rpc as unknown as (
    f: string, a: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { message?: string } | null }>)(fn, args);
  if (error) throw new Error(error.message || 'Reschedule is unavailable right now');
  return data as T;
}

export async function getRescheduleQuote(bookingId: string): Promise<RescheduleQuote> {
  return rescheduleRpc<RescheduleQuote>('rpc_reschedule_quote', { p_booking_id: bookingId });
}

export async function getRescheduleOptions(bookingId: string): Promise<{
  eligible: boolean; reason?: string | null; seat_count?: number;
  window_from?: string; window_to?: string; apply_deadline?: string;
  options: RescheduleOption[];
}> {
  const data = await rescheduleRpc<{
    eligible: boolean; reason?: string | null; seat_count?: number;
    window_from?: string; window_to?: string; apply_deadline?: string;
    options?: RescheduleOption[];
  }>('rpc_reschedule_options', { p_booking_id: bookingId });
  return { ...data, options: data?.options ?? [] };
}

export async function rescheduleBooking(params: {
  bookingId: string;
  departureId: string;
  seatIds: string[];
  boarding?: string | null;
  dropping?: string | null;
}): Promise<{ success: boolean; error?: string; travel_date?: string; departure_at?: string; seats?: string[] }> {
  return rescheduleRpc('rpc_reschedule_booking', {
    p_booking_id: params.bookingId,
    p_departure_id: params.departureId,
    p_seat_ids: params.seatIds,
    p_boarding: params.boarding ?? null,
    p_dropping: params.dropping ?? null,
  });
}
